import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Gauge, ScanLine, Save, ArrowRight, Wrench } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";

const LS_KEY = "h2-welcomed";

const Row = ({ icon: Icon, title, children }) => (
  <div className="flex gap-3">
    <div className="shrink-0 w-9 h-9 rounded-sm bg-amber/15 text-amber flex items-center justify-center">
      <Icon size={17} />
    </div>
    <div>
      <div className="font-heading font-semibold text-sm text-white">{title}</div>
      <div className="text-xs text-slate-400 leading-relaxed mt-0.5">{children}</div>
    </div>
  </div>
);

export const WelcomeDialog = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    try {
      if (!localStorage.getItem(LS_KEY)) setOpen(true);
    } catch { /* ignore */ }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
  };

  const viewGuide = () => { dismiss(); navigate("/help"); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="bg-surface border-steel text-white max-w-lg" data-testid="welcome-dialog">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-1">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-cyan to-blue-600 text-ink font-heading font-black text-base tracking-tighter shadow-lg shadow-cyan/30">TK</div>
            <div className="font-mono text-[10px] text-cyan tracking-widest uppercase">{t("welcome.tag")}</div>
          </div>
          <DialogTitle className="font-heading font-extrabold uppercase tracking-wide text-2xl">{t("welcome.title")}</DialogTitle>
          <DialogDescription className="text-slate-400">{t("welcome.body")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <Row icon={ScanLine} title={t("welcome.s1t")}>{t("welcome.s1b")}</Row>
          <Row icon={Gauge} title={t("welcome.s2t")}>{t("welcome.s2b")}</Row>
          <Row icon={Save} title={t("welcome.s3t")}>{t("welcome.s3b")}</Row>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button data-testid="welcome-start" onClick={dismiss} variant="outline"
            className="bg-elevated border-steel text-slate-200 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">
            <Wrench size={14} /> {t("welcome.start")}
          </Button>
          <Button data-testid="welcome-view-guide" onClick={viewGuide}
            className="bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold">
            {t("welcome.guide")} <ArrowRight size={14} />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
